import { tabKeyboard } from '@/lib/tabKeyboard';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type ReactElement,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  v2,
  type AgencyContactBlob,
  type CompanyProfile,
  type CompanyProfileFieldUsageMap,
  type DocumentTemplate,
  type ProfileVersionMove,
  type RegionalLetterhead,
  type SaveConcurrency,
} from '@/api/v2Client';
import {
  adoptVersion,
  fieldDifferences,
  readSaveConflict,
  type FieldDifference,
  type SaveConflict,
} from '@/lib/concurrency';
import { SaveConflictBanner } from '@/components/admin/company-profile/SaveConflictBanner';
// phase-small-findings-batch-1 (item 4) — the SAME preview surface the Form
// Templates screen uses. No second preview path is introduced.
import { TemplatePreviewModal } from '@/components/admin/form-templates/TemplatePreviewModal';
import { formatApiError } from '@/lib/errors';
import {
  LETTERHEAD_MAX_BYTES,
  LOGO_MAX_BYTES,
  oversizeMessage,
} from '@/lib/uploadLimits';
import {
  LETTERHEAD_MODES,
  LETTERHEAD_REGIONS,
  regionLabel,
  coverLetterTemplatesFor,
  letterheadModeGapMessage,
} from '@/lib/letterheadMode';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { EmptyState } from '@/components/dashboard/EmptyState';
import { Spinner } from '@/components/dashboard/Spinner';
import { ConfirmModal } from '@/components/shared/ConfirmModal';
import { useConfirmation } from '@/hooks/useConfirmation';
import type { CompanyProfileUsage } from '@/api/v2Client';
import { PageToast } from '@/components/shared/PageToast';
import { useUiToast } from '@/hooks/useUiToast';
import { formatDate } from '@/lib/format';
import { PermissionDenied } from '@/pages/PermissionDenied';
import { ProfileContactRows } from '@/components/admin/company-profile/ProfileContactRows';
import { Field, FieldUsageContext } from '@/components/admin/company-profile/FieldUsage';
import { RESIZE_GRIP_STYLE, useDraggableModal } from '@/hooks/useDraggableModal';
import { useModalA11y } from '@/hooks/useModalA11y';

// Backend (src/routes/company-profiles.ts) emits `{ error, message? }`
// for 4xx. ValidationError codes that ship with a message fall through
// to `parsed.message` so per-field detail is preserved; bare codes
// (NotFound, AuthRequired) get a friendlier rewrite via the map below.
// Phase E-polish-extract — the formatXxxError helper was hoisted to
// `@/lib/errors`; this map is passed in as the `codeMap` argument.
const COMPANY_PROFILE_ERROR_MESSAGES: Record<string, string> = {
  NotFound: 'This company profile no longer exists. The list may be out of date.',
  AuthRequired: 'Your session expired. Please sign in again.',
};

// Phase 5.X-company-profile-backend — per-agency card field sets. The
// contact triple (name/title/email) is common to every region; the
// agency-specific extras live alongside in the same JSON blob. Keys
// match V1's Company Profile screen so existing V1 callers stay in
// sync with what V2 captures.
// phase-profile-contact-rows — the Regulatory Contact triple
// (contactName/Title/Email) NO LONGER lives in these cards: contacts are now
// rows with a Function dimension, edited in the "Regulatory contacts" section
// (ProfileContactRows). What stays here is the genuinely agency-specific
// paperwork that is not a contact row — QPPV, the UK Responsible Person, the AU
// local contact — plus each card's scalar columns.
const AGENCY_CARD_FIELDS: Record<AgencyRegion, ReadonlyArray<readonly [string, string]>> = {
  fda: [],
  ema: [
    ['qppvName', 'QPPV Name'],
    ['qppvEmail', 'QPPV Email'],
  ],
  mhra: [['ukRpName', 'UK Responsible Person']],
  pmda: [],
  tga: [['auLocalContact', 'AU Local Contact']],
};

// Health Canada is absent: its card carried only the contact triple, which is
// now a contact row, so the card would render empty.
type AgencyRegion = 'fda' | 'ema' | 'mhra' | 'pmda' | 'tga';
const AGENCY_REGIONS: ReadonlyArray<{ key: AgencyRegion; label: string; field: keyof CompanyProfile }> = [
  { key: 'fda', label: 'FDA', field: 'FdaAgencyContact' },
  { key: 'ema', label: 'EMA', field: 'EmaAgencyContact' },
  { key: 'mhra', label: 'MHRA', field: 'MhraAgencyContact' },
  { key: 'pmda', label: 'PMDA', field: 'PmdaAgencyContact' },
  { key: 'tga', label: 'TGA', field: 'TgaAgencyContact' },
];

interface FormDraft {
  CompanyName: string;
  ProfileName: string;
  Agency: string;
  DunsNumber: string;
  FeiNumber: string;
  FdaEsgTradingPartnerId: string;
  EsgAccount: string;
  Street: string;
  City: string;
  Province: string;
  PostalCode: string;
  Country: string;
  Phone: string;
  Fax: string;
  ContactName: string;
  ContactTitle: string;
  ContactEmail: string;
  // phase-fix-signatory-and-submission-date — authorized signatory
  AuthorizedSignatoryName: string;
  AuthorizedSignatoryTitle: string;
  // Phase 5.X-company-profile-backend — Identity extras
  TradingName: string;
  Website: string;
  SecondaryContactName: string;
  SecondaryContactTitle: string;
  SecondaryContactEmail: string;
  // Agency-specific scalars (sister-flat columns, surfaced inside the
  // EMA / MHRA / PMDA / TGA agency cards alongside the JSON blob fields).
  EmaCode: string;
  Eudravigilance: string;
  UkLicence: string;
  // Phase 7.1 — MHRA/PMDA/TGA international representative fields.
  MhraUkRepresentative: string;
  PmdaLocalAgentName: string;
  PmdaLocalAgentAddress: string;
  PmdaLocalAgentPhone: string;
  PmdaLocalAgentEmail: string;
  TgaName: string;
  TgaSponsorNumber: string;
  // 6 per-region agency contact blobs, kept as { [field]: string } maps
  // in draft state; converted to nullable objects on save.
  fdaAgencyContact: Record<string, string>;
  hcAgencyContact: Record<string, string>;
  emaAgencyContact: Record<string, string>;
  mhraAgencyContact: Record<string, string>;
  pmdaAgencyContact: Record<string, string>;
  tgaAgencyContact: Record<string, string>;
  // Compliance Settings tab — string tri-state so the dropdown can
  // express "inherit" (=> null on save) vs "on" vs "off".
  RequireDualControlDelete: '' | 'true' | 'false';
  RequirePublishAttestation: '' | 'true' | 'false';
  RequireReauthAtPublish: '' | 'true' | 'false';
}

const EMPTY_AGENCY_CONTACT: Record<string, string> = {};

const EMPTY_DRAFT: FormDraft = {
  CompanyName: '',
  ProfileName: '',
  Agency: '',
  DunsNumber: '',
  FeiNumber: '',
  FdaEsgTradingPartnerId: '',
  EsgAccount: '',
  Street: '',
  City: '',
  Province: '',
  PostalCode: '',
  Country: '',
  Phone: '',
  Fax: '',
  ContactName: '',
  ContactTitle: '',
  ContactEmail: '',
  AuthorizedSignatoryName: '',
  AuthorizedSignatoryTitle: '',
  TradingName: '',
  Website: '',
  SecondaryContactName: '',
  SecondaryContactTitle: '',
  SecondaryContactEmail: '',
  EmaCode: '',
  Eudravigilance: '',
  UkLicence: '',
  MhraUkRepresentative: '',
  PmdaLocalAgentName: '',
  PmdaLocalAgentAddress: '',
  PmdaLocalAgentPhone: '',
  PmdaLocalAgentEmail: '',
  TgaName: '',
  TgaSponsorNumber: '',
  fdaAgencyContact: { ...EMPTY_AGENCY_CONTACT },
  hcAgencyContact: { ...EMPTY_AGENCY_CONTACT },
  emaAgencyContact: { ...EMPTY_AGENCY_CONTACT },
  mhraAgencyContact: { ...EMPTY_AGENCY_CONTACT },
  pmdaAgencyContact: { ...EMPTY_AGENCY_CONTACT },
  tgaAgencyContact: { ...EMPTY_AGENCY_CONTACT },
  RequireDualControlDelete: '',
  RequirePublishAttestation: '',
  RequireReauthAtPublish: '',
};

function agencyBlobToRecord(blob: AgencyContactBlob | null | undefined): Record<string, string> {
  if (!blob || typeof blob !== 'object') return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(blob)) {
    if (v === null || v === undefined) continue;
    out[k] = String(v);
  }
  return out;
}

function triStateFromBool(v: boolean | null | undefined): '' | 'true' | 'false' {
  if (v === true) return 'true';
  if (v === false) return 'false';
  return '';
}

function profileToDraft(p: CompanyProfile): FormDraft {
  return {
    CompanyName: p.CompanyName ?? '',
    ProfileName: p.ProfileName ?? '',
    Agency: p.Agency ?? '',
    DunsNumber: p.DunsNumber ?? '',
    FeiNumber: p.FeiNumber ?? '',
    FdaEsgTradingPartnerId: p.FdaEsgTradingPartnerId ?? '',
    EsgAccount: p.EsgAccount ?? '',
    Street: p.Street ?? '',
    City: p.City ?? '',
    Province: p.Province ?? '',
    PostalCode: p.PostalCode ?? '',
    Country: p.Country ?? '',
    Phone: p.Phone ?? '',
    Fax: p.Fax ?? '',
    ContactName: p.ContactName ?? '',
    ContactTitle: p.ContactTitle ?? '',
    ContactEmail: p.ContactEmail ?? '',
    AuthorizedSignatoryName: p.AuthorizedSignatoryName ?? '',
    AuthorizedSignatoryTitle: p.AuthorizedSignatoryTitle ?? '',
    TradingName: p.TradingName ?? '',
    Website: p.Website ?? '',
    SecondaryContactName: p.SecondaryContactName ?? '',
    SecondaryContactTitle: p.SecondaryContactTitle ?? '',
    SecondaryContactEmail: p.SecondaryContactEmail ?? '',
    EmaCode: p.EmaCode ?? '',
    Eudravigilance: p.Eudravigilance ?? '',
    UkLicence: p.UkLicence ?? '',
    MhraUkRepresentative: p.MhraUkRepresentative ?? '',
    PmdaLocalAgentName: p.PmdaLocalAgentName ?? '',
    PmdaLocalAgentAddress: p.PmdaLocalAgentAddress ?? '',
    PmdaLocalAgentPhone: p.PmdaLocalAgentPhone ?? '',
    PmdaLocalAgentEmail: p.PmdaLocalAgentEmail ?? '',
    TgaName: p.TgaName ?? '',
    TgaSponsorNumber: p.TgaSponsorNumber ?? '',
    fdaAgencyContact: agencyBlobToRecord(p.FdaAgencyContact),
    hcAgencyContact: agencyBlobToRecord(p.HcAgencyContact),
    emaAgencyContact: agencyBlobToRecord(p.EmaAgencyContact),
    mhraAgencyContact: agencyBlobToRecord(p.MhraAgencyContact),
    pmdaAgencyContact: agencyBlobToRecord(p.PmdaAgencyContact),
    tgaAgencyContact: agencyBlobToRecord(p.TgaAgencyContact),
    RequireDualControlDelete: triStateFromBool(p.RequireDualControlDelete),
    RequirePublishAttestation: triStateFromBool(p.RequirePublishAttestation),
    RequireReauthAtPublish: triStateFromBool(p.RequireReauthAtPublish),
  };
}

// Convert empty strings to null so the backend treats blanks as cleared.
// CompanyName is the only required field at the backend so it must always
// be a string (never null) in the payload.
/**
 * Tracker row 14 — the values an editor loaded, sent as a save's `baseline` so
 * a refusal can name exactly the fields someone else changed (identity and
 * timestamp columns are not fields anyone edits).
 */
const NON_FIELD_KEYS: ReadonlySet<string> = new Set([
  'Id',
  'TenantId',
  'CreatedAt',
  'UpdatedAt',
  'DeletedAt',
]);
function profileBaseline(p: CompanyProfile): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(p as unknown as Record<string, unknown>).filter(([k]) => !NON_FIELD_KEYS.has(k)),
  );
}

function draftToPayload(draft: FormDraft): Partial<CompanyProfile> {
  const out: Record<string, unknown> = {};
  const SCALAR_KEYS = [
    'CompanyName', 'ProfileName', 'Agency',
    'DunsNumber', 'FeiNumber', 'FdaEsgTradingPartnerId', 'EsgAccount',
    'Street', 'City', 'Province', 'PostalCode', 'Country',
    'Phone', 'Fax', 'ContactName', 'ContactTitle', 'ContactEmail',
    'AuthorizedSignatoryName', 'AuthorizedSignatoryTitle',
    'TradingName', 'Website',
    'SecondaryContactName', 'SecondaryContactTitle', 'SecondaryContactEmail',
    'EmaCode', 'Eudravigilance', 'UkLicence',
    'MhraUkRepresentative',
    'PmdaLocalAgentName', 'PmdaLocalAgentAddress', 'PmdaLocalAgentPhone', 'PmdaLocalAgentEmail',
    'TgaName', 'TgaSponsorNumber',
  ] as const;
  for (const k of SCALAR_KEYS) {
    const v = draft[k];
    if (k === 'CompanyName') out[k] = v;
    else out[k] = v === '' ? null : v;
  }
  // Agency contact blobs: send the populated object, or null to clear.
  const blobMap: ReadonlyArray<[keyof FormDraft, keyof CompanyProfile]> = [
    ['fdaAgencyContact', 'FdaAgencyContact'],
    ['hcAgencyContact', 'HcAgencyContact'],
    ['emaAgencyContact', 'EmaAgencyContact'],
    ['mhraAgencyContact', 'MhraAgencyContact'],
    ['pmdaAgencyContact', 'PmdaAgencyContact'],
    ['tgaAgencyContact', 'TgaAgencyContact'],
  ];
  for (const [draftKey, columnKey] of blobMap) {
    const obj = draft[draftKey] as Record<string, string>;
    const cleaned: Record<string, string> = {};
    for (const [k, v] of Object.entries(obj)) {
      if (v !== '') cleaned[k] = v;
    }
    out[columnKey] = Object.keys(cleaned).length === 0 ? null : cleaned;
  }
  // Compliance tri-states: '' → null (inherit), 'true' → true, 'false' → false.
  for (const k of ['RequireDualControlDelete', 'RequirePublishAttestation', 'RequireReauthAtPublish'] as const) {
    const v = draft[k];
    out[k] = v === '' ? null : v === 'true';
  }
  return out as Partial<CompanyProfile>;
}

const tdStyle: CSSProperties = { padding: '12px 8px', verticalAlign: 'top' };

interface ModalState {
  readonly mode: 'create' | 'edit';
  readonly profile: CompanyProfile | null;
}

// Phase E-polish-extract-3 — inlined ConfirmModal hoisted to
// `@/components/shared/ConfirmModal`.

export function CompanyProfilePage(): ReactElement {
  const perms = useUserPermissions();
  const canRead = perms.hasPermission('CompanyProfiles', 'read');
  const canWrite = perms.hasPermission('CompanyProfiles', 'write');
  const canDelete = perms.hasPermission('CompanyProfiles', 'delete');

  const [profiles, setProfiles] = useState<CompanyProfile[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CompanyProfile | null>(null);
  // phase-entity-model-stage-1-delete-safety — real usage, fetched when the
  // delete dialog opens. `null` = still loading; the dialog never offers a
  // confirm button until the answer is in, so nobody confirms blind.
  const [deleteUsage, setDeleteUsage] = useState<CompanyProfileUsage | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [deletedProfiles, setDeletedProfiles] = useState<CompanyProfile[] | null>(null);
  const { toast, showToast, clearToast } = useUiToast();

  // Fetch usage whenever a delete is contemplated.
  useEffect(() => {
    if (!deleteTarget) {
      setDeleteUsage(null);
      return;
    }
    let cancelled = false;
    v2.companyProfiles
      .usage(deleteTarget.Id)
      .then((u) => {
        if (!cancelled) setDeleteUsage(u);
      })
      .catch(() => {
        // Unknown usage must not read as "no usage" — leave it null so the
        // dialog says it could not check rather than implying it is safe.
        if (!cancelled) setDeleteUsage(null);
      });
    return () => {
      cancelled = true;
    };
  }, [deleteTarget]);

  // The deleted list, loaded on demand.
  useEffect(() => {
    if (!showDeleted) return;
    let cancelled = false;
    v2.companyProfiles
      .listDeleted()
      .then((rows) => {
        if (!cancelled) setDeletedProfiles(rows);
      })
      .catch(() => {
        if (!cancelled) setDeletedProfiles([]);
      });
    return () => {
      cancelled = true;
    };
  }, [showDeleted, reloadKey]);

  useEffect(() => {
    if (!canRead) return;
    setProfiles(null);
    setError(null);
    v2.companyProfiles
      .list()
      .then(setProfiles)
      .catch((err: unknown) => {
        setError(formatApiError(err, 'Failed to load company profiles', COMPANY_PROFILE_ERROR_MESSAGES));
        setProfiles([]);
      });
  }, [canRead, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  // phase-company-profile-rows-7-9 (Row 7) — `?edit=<id>&section=signatories`
  // (the Forms tab's "Add an authorized signatory" link) opens that profile's
  // editor at its signatory list. The params are cleared when the editor
  // closes, so it does not reopen.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkId = searchParams.get('edit');
  const deepLinkSection = searchParams.get('section');
  // Open each deep link ONCE. Closing the editor clears the params, but the
  // effect can run (modal now null, params not yet cleared) before that lands —
  // without this guard a saved/closed editor reopened itself.
  const openedDeepLink = useRef<string | null>(null);
  useEffect(() => {
    if (!deepLinkId || !profiles || modal || openedDeepLink.current === deepLinkId) return;
    const target = profiles.find((p) => p.Id === deepLinkId);
    if (target) {
      openedDeepLink.current = deepLinkId;
      setModal({ mode: 'edit', profile: target });
    }
  }, [deepLinkId, profiles, modal]);
  const closeModal = useCallback(() => {
    setModal(null);
    if (deepLinkId) setSearchParams({}, { replace: true });
  }, [deepLinkId, setSearchParams]);

  const handleDeleteConfirmed = async (p: CompanyProfile): Promise<void> => {
    setDeleteTarget(null);
    try {
      await v2.companyProfiles.delete(p.Id);
      reload();
      showToast({ type: 'success', text: `Deleted profile "${p.CompanyName}".` });
    } catch (err) {
      showToast({
        type: 'error',
        text: formatApiError(err, 'Failed to delete profile', COMPANY_PROFILE_ERROR_MESSAGES),
      });
    }
  };

  const handleRestore = async (p: CompanyProfile): Promise<void> => {
    try {
      await v2.companyProfiles.restore(p.Id);
      reload();
      showToast({ type: 'success', text: `Restored profile "${p.CompanyName}".` });
    } catch (err) {
      showToast({
        type: 'error',
        text: formatApiError(err, 'Failed to restore profile', COMPANY_PROFILE_ERROR_MESSAGES),
      });
    }
  };

  if (perms.loading) return <Spinner caption="Checking permissions…" />;
  if (!canRead) return <PermissionDenied />;

  return (
    <section>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <div>
          {/* phase-v1-ui-parity — V1 CompanyProfileScreen titles this
              "Company Profiles" (plural) via .screen-title. */}
          <h1 className="screen-title" style={{ margin: 0 }}>Company Profiles</h1>
          <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)' }}>
            Organization identity, FDA ESG credentials, and signing-officer contact details.
          </p>
        </div>
        {canWrite && (
          <button
            type="button"
            onClick={() => setModal({ mode: 'create', profile: null })}
            style={{
              background: 'var(--accent)',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              padding: '8px 14px',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            + New profile
          </button>
        )}
      </div>

      {profiles === null && !error && <Spinner caption="Loading profiles…" />}
      {error && (
        <div role="alert" style={{ color: 'var(--red)', marginBottom: 12 }}>
          {error}
          <button
            type="button"
            onClick={reload}
            style={{ marginLeft: 12, padding: '4px 12px', cursor: 'pointer' }}
          >
            Retry
          </button>
        </div>
      )}

      {profiles !== null && profiles.length === 0 && !error && (
        <EmptyState
          icon="🏢"
          heading="No company profiles yet"
          subtext="Create a profile to capture organization identity, ESG credentials, and contact details."
          {...(canWrite && {
            action: {
              label: '+ Create first profile',
              onClick: () => setModal({ mode: 'create', profile: null }),
            },
          })}
        />
      )}

      {profiles && profiles.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border)', textAlign: 'left' }}>
              <th style={tdStyle}>Company</th>
              <th style={tdStyle}>Contact</th>
              <th style={tdStyle}>Updated</th>
              {(canWrite || canDelete) && <th style={tdStyle}></th>}
            </tr>
          </thead>
          <tbody>
            {profiles.map((p) => (
              /* phase-ui-company-profiles-improvements (Item 1) — the whole row
                 opens the profile (same modal as Edit). The Edit/Delete buttons
                 stopPropagation so they don't double-trigger.
                 phase-ui-review-F15 — opening that modal is EDITING (it offers
                 Save, which the server would refuse), so the row only opens it
                 when the caller has CompanyProfiles:write. For a read-only
                 viewer the row is inert — consistent with the hidden Edit
                 button — and does not broaden anyone's permissions. */
              <tr
                key={p.Id}
                style={{
                  borderBottom: '1px solid var(--border)',
                  cursor: canWrite ? 'pointer' : 'default',
                }}
                data-testid={`profile-row-${p.Id}`}
                onClick={canWrite ? () => setModal({ mode: 'edit', profile: p }) : undefined}
                onMouseEnter={(e) => {
                  if (!canWrite) return;
                  (e.currentTarget as HTMLTableRowElement).style.background = 'var(--surface2, rgba(0,0,0,0.03))';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLTableRowElement).style.background = 'transparent';
                }}
              >
                <td style={tdStyle}>
                  <div style={{ fontWeight: 600 }}>{p.CompanyName}</div>
                  {p.ProfileName && (
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{p.ProfileName}</div>
                  )}
                </td>
                <td style={{ ...tdStyle, fontSize: 13 }}>
                  {p.ContactEmail ?? p.ContactName ?? '—'}
                </td>
                <td style={{ ...tdStyle, fontSize: 13, color: 'var(--text-muted)' }}>
                  {formatDate(p.UpdatedAt)}
                </td>
                {(canWrite || canDelete) && (
                  <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                    {canWrite && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setModal({ mode: 'edit', profile: p });
                        }}
                        style={{
                          background: 'none',
                          color: 'var(--accent)',
                          border: '1px solid var(--accent)',
                          borderRadius: 4,
                          padding: '4px 10px',
                          marginRight: 8,
                          cursor: 'pointer',
                          fontSize: 12,
                        }}
                      >
                        Edit
                      </button>
                    )}
                    {canDelete && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(p);
                        }}
                        data-testid={`profile-delete-btn-${p.Id}`}
                        style={{
                          background: 'none',
                          color: 'var(--red)',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: 12,
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* phase-entity-model-stage-1-delete-safety — RECOVERY MADE VISIBLE.
          Deletion has always been soft, but nothing surfaced that: recovery
          meant editing the database directly. */}
      {canDelete && (
        <div style={{ marginTop: 24 }}>
          <button
            type="button"
            data-testid="profile-show-deleted-toggle"
            onClick={() => setShowDeleted((v) => !v)}
            style={{
              background: 'none',
              border: '1px solid var(--border)',
              borderRadius: 6,
              padding: '6px 12px',
              fontSize: 13,
              cursor: 'pointer',
              color: 'var(--text-muted)',
            }}
          >
            {showDeleted ? 'Hide deleted profiles' : 'Show deleted profiles'}
          </button>

          {showDeleted && (
            <div data-testid="profile-deleted-list" style={{ marginTop: 12 }}>
              {deletedProfiles === null ? (
                <Spinner caption="Loading deleted profiles…" />
              ) : deletedProfiles.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  No deleted profiles. Deleting a profile here is reversible —
                  deleted profiles appear in this list and can be restored.
                </p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <tbody>
                    {deletedProfiles.map((p) => (
                      <tr
                        key={p.Id}
                        data-testid={`deleted-profile-row-${p.Id}`}
                        style={{ borderBottom: '1px solid var(--border)' }}
                      >
                        <td style={{ padding: '8px 10px' }}>
                          <strong>{p.CompanyName}</strong>
                          <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>
                            deleted
                          </span>
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                          <button
                            type="button"
                            data-testid={`profile-restore-${p.Id}`}
                            onClick={() => void handleRestore(p)}
                            style={{
                              background: 'var(--accent)',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 4,
                              padding: '4px 12px',
                              fontSize: 12,
                              cursor: 'pointer',
                            }}
                          >
                            Restore
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}

      {modal && (
        <ProfileEditModal
          mode={modal.mode}
          profile={modal.profile}
          canManageBranding={perms.hasPermission('Users', 'manage')}
          focusSection={modal.profile?.Id === deepLinkId ? deepLinkSection : null}
          onClose={closeModal}
          onSaved={(savedName) => {
            const wasCreate = modal.mode === 'create';
            closeModal();
            reload();
            showToast({
              type: 'success',
              text: wasCreate
                ? `Created profile "${savedName}".`
                : `Saved changes to "${savedName}".`,
            });
          }}
        />
      )}
      {deleteTarget && (
        /* phase-entity-model-stage-1-delete-safety — an HONEST delete dialog.
           It used to claim "existing submissions referencing it will keep
           their snapshot", which was false: there is no snapshot. The
           resolver reads the profile live, so deleting a linked profile
           silently changed whose company name went on generated documents.
           The dialog now shows REAL usage before anything is confirmed, and
           a profile in use cannot be deleted at all (the server refuses with
           the same count — one evaluator, two surfaces). */
        <ConfirmModal
          title="Delete company profile"
          message={
            deleteUsage === null
              ? `Checking what still uses "${deleteTarget.CompanyName}"…`
              : deleteUsage.submissionCount > 0
                ? `"${deleteTarget.CompanyName}" cannot be deleted: ${deleteUsage.submissionCount} submission${deleteUsage.submissionCount === 1 ? '' : 's'} still link to it (${deleteUsage.submissions.map((x) => x.label).join(', ')}${deleteUsage.truncated ? ', …' : ''}). Link those submissions to a different company profile first — deleting this one would put the wrong company on their documents.`
                : `Delete profile "${deleteTarget.CompanyName}"? Nothing links to it. Deletion is reversible — it will appear under "Show deleted" and can be restored.`
          }
          confirmLabel={deleteUsage && deleteUsage.submissionCount > 0 ? 'Cannot delete' : 'Delete'}
          variant="destructive"
          // Cancel stays available throughout; only Confirm waits.
          confirmDisabled={deleteUsage === null || deleteUsage.submissionCount > 0}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => void handleDeleteConfirmed(deleteTarget)}
        />
      )}
      <PageToast toast={toast} onDismiss={clearToast} />
    </section>
  );
}

interface ModalProps {
  readonly mode: 'create' | 'edit';
  readonly profile: CompanyProfile | null;
  readonly canManageBranding: boolean;
  /** phase-company-profile-rows-7-9 — scroll to this section on open
   * ('signatories' from the Forms tab's "Add an authorized signatory" link). */
  readonly focusSection?: string | null;
  readonly onClose: () => void;
  readonly onSaved: (savedName: string) => void;
}

function ProfileEditModal({
  mode,
  profile,
  canManageBranding,
  focusSection = null,
  onClose,
  onSaved,
}: ModalProps): ReactElement {
  const [draft, setDraft] = useState<FormDraft>(() =>
    profile ? profileToDraft(profile) : EMPTY_DRAFT,
  );
  // phase-company-profile-rows-7-9 (Row 7) — arrive at the signatory list.
  const signatoriesRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focusSection === 'signatories') signatoriesRef.current?.scrollIntoView?.({ block: 'start' });
  }, [focusSection]);
  // phase-ui-company-profiles-improvements (Item 4) — the derived field-usage
  // map for the ⓘ tooltips. Fetched once per modal open; failure degrades to
  // no icons (null context), never an error state.
  const [fieldUsage, setFieldUsage] = useState<CompanyProfileFieldUsageMap | null>(null);
  useEffect(() => {
    let cancelled = false;
    void v2.companyProfiles
      .fieldUsage()
      .then((map) => {
        if (!cancelled) setFieldUsage(map);
      })
      .catch(() => {
        /* icons simply don't render */
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Tracker row 14 — optimistic concurrency. `version` is the UpdatedAt this
  // editor was opened from (sent with every save, replaced by each save's new
  // one); `loaded` is what was loaded, sent as the baseline so a refusal names
  // exactly what someone else changed; `conflict` is a refused save — the form
  // stays open with the user's edits, nothing is overwritten.
  const [loaded, setLoaded] = useState<CompanyProfile | null>(profile);
  const [version, setVersion] = useState<string | null>(profile?.UpdatedAt ?? null);
  const [conflict, setConflict] = useState<SaveConflict | null>(null);
  // Phase FIX-company-profile-ux-and-assets — branding state for per-profile logo/letterhead
  const [brandingLogoUrl, setBrandingLogoUrl] = useState<string | null>(
    profile?.CompanyLogoUrl ?? null,
  );
  const [brandingLetterheadUrl, setBrandingLetterheadUrl] = useState<string | null>(
    profile?.LetterheadTemplateUrl ?? null,
  );
  const [letterheadMode, setLetterheadMode] = useState<string>(
    profile?.LetterheadMode ?? 'none',
  );
  const [brandingError, setBrandingError] = useState<string | null>(null);
  const [brandingUploading, setBrandingUploading] = useState(false);
  // phase-small-findings-batch-1 (item 3) — the missing-asset state.
  const letterheadModeGap = letterheadModeGapMessage(
    letterheadMode,
    brandingLogoUrl !== null,
    brandingLetterheadUrl !== null,
  );
  // phase-small-findings-batch-1 (item 4) — cover-letter templates available
  // to preview against THIS profile. Fetched once when the branding section
  // is on screen; a failure just leaves the affordance saying so.
  const [coverLetterTemplates, setCoverLetterTemplates] = useState<DocumentTemplate[]>([]);
  const [coverLetterTemplateId, setCoverLetterTemplateId] = useState<string>('');
  const [previewTemplate, setPreviewTemplate] = useState<DocumentTemplate | null>(null);
  const brandingVisible = mode === 'edit' && Boolean(profile?.Id);
  useEffect(() => {
    if (!brandingVisible) return;
    let cancelled = false;
    v2.documentTemplates
      .list()
      .then((rows) => {
        if (cancelled) return;
        const matches = coverLetterTemplatesFor(rows);
        setCoverLetterTemplates(matches);
        setCoverLetterTemplateId(matches[0]?.Id ?? '');
      })
      .catch(() => {
        // Non-fatal: the affordance reports that none is available.
      });
    return (): void => {
      cancelled = true;
    };
  }, [brandingVisible]);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const letterheadInputRef = useRef<HTMLInputElement>(null);

  // phase-regional-letterhead-templates (TODO-077) — per-region variants
  const [regionalLetterheads, setRegionalLetterheads] = useState<RegionalLetterhead[]>([]);
  const [regionalLoading, setRegionalLoading] = useState(false);
  const [regionalError, setRegionalError] = useState<string | null>(null);
  const [showAddRegional, setShowAddRegional] = useState(false);
  const [draftRegion, setDraftRegion] = useState('FDA');
  const [draftLetterheadUrl, setDraftLetterheadUrl] = useState('');
  const [draftLabel, setDraftLabel] = useState('');
  const [savingRegional, setSavingRegional] = useState(false);
  // phase-company-profile-rows-7-9 (Row 9) — a regional letterhead is an
  // uploaded FILE (the only kind the generator reads) or a storage key naming a
  // file already uploaded to this tenant; never an external URL (T17-D16).
  const [draftFile, setDraftFile] = useState<File | null>(null);
  const regionalFileRef = useRef<HTMLInputElement>(null);
  // The regional row whose file is the profile default, if any — the same
  // equality the server's isDefaultLetterhead uses.
  const defaultRegional =
    brandingLetterheadUrl === null
      ? null
      : (regionalLetterheads.find((r) => r.LetterheadUrl === brandingLetterheadUrl) ?? null);

  useEffect(() => {
    if (!profile?.Id) return;
    let cancelled = false;
    setRegionalLoading(true);
    setRegionalError(null);
    void v2.companyProfiles
      .listLetterheads(profile.Id)
      .then((res) => {
        if (!cancelled) setRegionalLetterheads(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) setRegionalError(formatApiError(err, 'Failed to load regional letterheads'));
      })
      .finally(() => {
        if (!cancelled) setRegionalLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [profile?.Id]);

  const handleSaveRegionalLetterhead = async (): Promise<void> => {
    if (!profile?.Id) return;
    if (!draftRegion.trim() || (!draftFile && !draftLetterheadUrl.trim())) {
      setRegionalError('Choose a region, then a letterhead file to upload (or a storage key for a file already uploaded).');
      return;
    }
    if (draftFile) {
      const tooBig = oversizeMessage(draftFile, LETTERHEAD_MAX_BYTES, 'Letterhead');
      if (tooBig) {
        setRegionalError(tooBig);
        return;
      }
    }
    setSavingRegional(true);
    setRegionalError(null);
    const label = draftLabel.trim() === '' ? null : draftLabel.trim();
    try {
      // A file wins over a typed key: uploading stores the bytes and returns
      // its storage key. The key field references a file ALREADY uploaded to
      // this organisation; the server validates it is a tenant storage key
      // (T17-D16) and refuses a URL or a foreign-tenant key. Every server
      // refusal — wrong type, corrupt file, unknown region, non-key — is shown
      // exactly as the server words it (the client never rewrites the value).
      const res = draftFile
        ? await v2.companyProfiles.uploadRegionalLetterhead(profile.Id, draftRegion.trim(), draftFile, label)
        : await v2.companyProfiles.upsertLetterhead(profile.Id, {
            region: draftRegion.trim(),
            letterheadUrl: draftLetterheadUrl.trim(),
            label,
          });
      setRegionalLetterheads((prev) => {
        const filtered = prev.filter(
          (r) => r.Region.toLowerCase() !== res.Region.toLowerCase(),
        );
        return [...filtered, res].sort((a, b) => a.Region.localeCompare(b.Region));
      });
      // Replacing the default region's file moves the default with it
      // (the server does the same).
      if (defaultRegional && defaultRegional.Region === res.Region) {
        setBrandingLetterheadUrl(res.LetterheadUrl);
        ownBrandingWrite(res.profileVersion, { LetterheadTemplateUrl: res.LetterheadUrl });
      }
      setShowAddRegional(false);
      setDraftRegion('FDA');
      setDraftLetterheadUrl('');
      setDraftLabel('');
      setDraftFile(null);
    } catch (err) {
      setRegionalError(formatApiError(err, 'Save failed'));
    } finally {
      setSavingRegional(false);
    }
  };

  // phase-company-profile-rows-7-9 (Row 9) — choose the default. One default
  // per profile (a single column), so this replaces the previous one.
  const handleSetDefaultLetterhead = async (row: RegionalLetterhead): Promise<void> => {
    if (!profile?.Id) return;
    setRegionalError(null);
    try {
      const res = await v2.companyProfiles.setDefaultLetterhead(profile.Id, row.Id);
      // The badge below is derived from this one value, so it moves too.
      setBrandingLetterheadUrl(res.letterheadUrl);
      ownBrandingWrite(res.profileVersion, { LetterheadTemplateUrl: res.letterheadUrl });
    } catch (err) {
      setRegionalError(formatApiError(err, 'Could not set the default letterhead'));
    }
  };

  const handleDeleteRegionalLetterhead = async (letterheadId: string): Promise<void> => {
    if (!profile?.Id) return;
    setRegionalError(null);
    try {
      await v2.companyProfiles.deleteRegionalLetterhead(profile.Id, letterheadId);
      setRegionalLetterheads((prev) => prev.filter((r) => r.Id !== letterheadId));
    } catch (err) {
      setRegionalError(formatApiError(err, 'Delete failed'));
    }
  };
  // Phase 5.X-company-profile-backend — tabbed modal: Profile | Compliance.
  const [activeTab, setActiveTab] = useState<'profile' | 'compliance'>('profile');
  // Phase 5.X-company-profile-backend — each regional agency card is
  // independently collapsible to keep the modal scroll manageable.
  const [agencyOpen, setAgencyOpen] = useState<Record<AgencyRegion, boolean>>({
    fda: false, ema: false, mhra: false, pmda: false, tga: false,
  });

  const update = <K extends keyof FormDraft>(k: K, v: FormDraft[K]): void =>
    setDraft((d) => ({ ...d, [k]: v }));

  const updateAgencyField = (
    region: AgencyRegion,
    field: string,
    value: string,
  ): void => {
    setDraft((d) => {
      const key = `${region}AgencyContact` as keyof FormDraft;
      const next = { ...(d[key] as Record<string, string>), [field]: value };
      return { ...d, [key]: next };
    });
  };

  // Phase FIX-company-profile-ux-and-assets — branding upload handlers.
  // Only available when editing an existing profile (profile.Id exists).
  const handleLogoUpload = async (file: File): Promise<void> => {
    if (!profile?.Id) return;
    setBrandingError(null);
    // phase-small-findings-batch-1 (item 5) — reject BEFORE uploading. The
    // server enforces the same cap and stays the authority; this just spares
    // the reader an upload that was always going to be refused.
    const tooBig = oversizeMessage(file, LOGO_MAX_BYTES, 'Logo');
    if (tooBig) {
      setBrandingError(tooBig);
      return;
    }
    setBrandingUploading(true);
    try {
      const result = await v2.companyProfiles.uploadLogo(profile.Id, file);
      setBrandingLogoUrl(result.logoUrl);
      ownBrandingWrite(result.profileVersion, { CompanyLogoUrl: result.logoUrl });
    } catch (err) {
      setBrandingError(formatApiError(err, 'Logo upload failed'));
    } finally {
      setBrandingUploading(false);
    }
  };

  const handleLogoDelete = async (): Promise<void> => {
    if (!profile?.Id) return;
    setBrandingError(null);
    setBrandingUploading(true);
    try {
      const result = await v2.companyProfiles.deleteLogo(profile.Id);
      setBrandingLogoUrl(null);
      ownBrandingWrite(result?.profileVersion, { CompanyLogoUrl: null });
    } catch (err) {
      setBrandingError(formatApiError(err, 'Logo delete failed'));
    } finally {
      setBrandingUploading(false);
    }
  };

  const handleLetterheadUpload = async (file: File): Promise<void> => {
    if (!profile?.Id) return;
    setBrandingError(null);
    // phase-small-findings-batch-1 (item 5) — see handleLogoUpload.
    const tooBig = oversizeMessage(file, LETTERHEAD_MAX_BYTES, 'Letterhead');
    if (tooBig) {
      setBrandingError(tooBig);
      return;
    }
    setBrandingUploading(true);
    try {
      const result = await v2.companyProfiles.uploadLetterhead(profile.Id, file);
      setBrandingLetterheadUrl(result.letterheadUrl);
      // Default mode to logo_only if not already set to full_letterhead.
      const nextMode = letterheadMode === 'full_letterhead' ? 'full_letterhead' : 'logo_only';
      setLetterheadMode(nextMode);
      ownBrandingWrite(result.profileVersion, {
        LetterheadTemplateUrl: result.letterheadUrl,
        LetterheadMode: nextMode,
      });
    } catch (err) {
      setBrandingError(formatApiError(err, 'Letterhead upload failed'));
    } finally {
      setBrandingUploading(false);
    }
  };

  const handleLetterheadDelete = async (): Promise<void> => {
    if (!profile?.Id) return;
    setBrandingError(null);
    setBrandingUploading(true);
    try {
      const result = await v2.companyProfiles.deleteLetterhead(profile.Id);
      setBrandingLetterheadUrl(null);
      setLetterheadMode('none');
      ownBrandingWrite(result?.profileVersion, { LetterheadTemplateUrl: null, LetterheadMode: 'none' });
    } catch (err) {
      setBrandingError(formatApiError(err, 'Letterhead delete failed'));
    } finally {
      setBrandingUploading(false);
    }
  };

  // phase-company-profile-rows-7-9 (Row 9) — this used to swallow every
  // failure ("best-effort"): the server refused the PATCH outright ("No
  // updatable fields supplied" — LetterheadMode was not writable), the select
  // kept showing the new value, and a reload silently showed the old one. Now
  // a failure is shown and the select goes back to what is actually saved.
  const [letterheadModeSaving, setLetterheadModeSaving] = useState(false);
  const [letterheadModeSaved, setLetterheadModeSaved] = useState(false);
  const handleLetterheadModeChange = async (newMode: string): Promise<void> => {
    if (!profile?.Id) return;
    const previous = letterheadMode;
    setLetterheadMode(newMode);
    setBrandingError(null);
    setLetterheadModeSaved(false);
    setLetterheadModeSaving(true);
    try {
      // Tracker row 14 — versioned like every profile save.
      const saved = await v2.companyProfiles.update(
        profile.Id,
        { LetterheadMode: newMode },
        saveConcurrency(),
      );
      setVersion(saved.UpdatedAt ?? null);
      setLoaded(saved);
      setLetterheadModeSaved(true);
    } catch (err) {
      setLetterheadMode(previous);
      const refused = readSaveConflict(err);
      if (refused) {
        setConflict(refused);
        setBrandingError(
          'Letterhead mode was not saved: this profile was changed by someone else — see the notice at the top of the form.',
        );
      } else {
        setBrandingError(formatApiError(err, 'Letterhead mode could not be saved'));
      }
    } finally {
      setLetterheadModeSaving(false);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!draft.CompanyName.trim()) {
      setError('Company name is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = draftToPayload(draft);
      if (mode === 'create') {
        await v2.companyProfiles.create(payload);
      } else if (profile) {
        await v2.companyProfiles.update(profile.Id, payload, saveConcurrency());
      }
      onSaved(draft.CompanyName);
    } catch (err) {
      // Tracker row 14 — a stale save is refused: keep the form open with the
      // user's edits and say so; never overwrite automatically.
      const refused = readSaveConflict(err);
      if (refused) setConflict(refused);
      else setError(formatApiError(err, 'Save failed', COMPANY_PROFILE_ERROR_MESSAGES));
    } finally {
      setSaving(false);
    }
  };

  // ── Tracker row 14 — optimistic concurrency helpers ────────────────────
  /** What every save of this editor carries: its version + what it loaded. */
  const saveConcurrency = (): SaveConcurrency => ({
    expectedUpdatedAt: version ?? '',
    ...(loaded ? { baseline: profileBaseline(loaded) } : {}),
  });

  /**
   * After one of this editor's own branding writes: adopt the new version only
   * if the write replaced the version held (otherwise someone else wrote in
   * between, and the next save must report it), and fold the change into what
   * counts as "loaded".
   */
  const ownBrandingWrite = (
    move: ProfileVersionMove | undefined,
    patch: Partial<CompanyProfile>,
  ): void => {
    if (!move) return;
    if (adoptVersion(version, move) !== version) {
      setVersion(move.current);
      setLoaded((l) => (l ? { ...l, ...patch } : l));
    }
  };

  /** "Reload (lose my edits)": continue from what is stored now. */
  const reloadFromCurrent = (): void => {
    if (!conflict) return;
    const current = conflict.current as unknown as CompanyProfile;
    setDraft(profileToDraft(current));
    setLoaded(current);
    setVersion(current.UpdatedAt ?? null);
    setBrandingLogoUrl(current.CompanyLogoUrl ?? null);
    setBrandingLetterheadUrl(current.LetterheadTemplateUrl ?? null);
    setLetterheadMode(current.LetterheadMode ?? 'none');
    setConflict(null);
    setError(null);
    setBrandingError(null);
  };

  /** "Review differences": each changed field, your value vs the stored value. */
  const conflictDifferences = (): FieldDifference[] => {
    if (!conflict) return [];
    const payload = draftToPayload(draft) as Record<string, unknown>;
    const yours = (field: string): unknown => {
      if (field in payload) return payload[field];
      if (field === 'CompanyLogoUrl') return brandingLogoUrl;
      if (field === 'LetterheadTemplateUrl') return brandingLetterheadUrl;
      if (field === 'LetterheadMode') return letterheadMode;
      return (loaded as Record<string, unknown> | null)?.[field];
    };
    return fieldDifferences(conflict.changedFields, yours, conflict.current);
  };

  const inputStyle: CSSProperties = {
    width: '100%',
    padding: '6px 10px',
    border: '1px solid var(--border)',
    borderRadius: 6,
    fontSize: 13,
  };

  // phase-draggable-resizable-editor-modal — this is a HEAVY editor, so it
  // opts into the draggable/resizable window behaviour (Sharon's ruling:
  // editors yes, confirm/alert dialogs no). Geometry persists per browser
  // under `autoectd.modalGeometry.company-profile-editor` and is clamped
  // back into the viewport on open; double-clicking the title bar resets.
  const drag = useDraggableModal({
    persistKey: 'company-profile-editor',
    minSize: { width: 560, height: 440 },
  });

  // phase-ui-review-F13 — closing a dirty editor (Cancel, the × , the backdrop
  // or Escape) used to drop the edits silently. Route every close affordance
  // through the shared ConfirmModal (NOT a native confirm()) so a person is
  // asked before losing work; a clean editor still closes immediately, and the
  // Save path closes via onSaved without ever prompting.
  const { ask, dialog: discardDialog } = useConfirmation();
  const initialDraftRef = useRef<FormDraft | null>(null);
  if (initialDraftRef.current === null) {
    initialDraftRef.current = profile ? profileToDraft(profile) : EMPTY_DRAFT;
  }
  const isDirty = JSON.stringify(draft) !== JSON.stringify(initialDraftRef.current);
  const requestClose = useCallback((): void => {
    if (saving) return;
    if (!isDirty) {
      onClose();
      return;
    }
    void ask({
      title: 'Discard unsaved changes?',
      message:
        'This company profile has changes that have not been saved. Close anyway and lose them?',
      confirmLabel: 'Discard changes',
      variant: 'destructive',
    }).then((ok) => {
      if (ok) onClose();
    });
  }, [saving, isDirty, onClose, ask]);

  const a11y = useModalA11y({ onClose: requestClose, disabled: saving });
  const { windowRef: dragWindowRef } = drag;
  const { ref: a11yRef } = a11y;
  const setWindowEl = useCallback(
    (el: HTMLElement | null): void => {
      dragWindowRef(el);
      a11yRef(el);
    },
    [dragWindowRef, a11yRef],
  );

  return (
    <FieldUsageContext.Provider value={fieldUsage}>
    {discardDialog}
    <div
      onClick={requestClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 300,
      }}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-label={mode === 'create' ? 'New company profile' : 'Edit company profile'}
        ref={setWindowEl}
        tabIndex={a11y.tabIndex}
        onKeyDown={a11y.onKeyDown}
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          borderRadius: 10,
          // Small-viewport fallback: near-full-screen centred sheet.
          // Otherwise: the original footprint, overridable by resize.
          ...(drag.smallViewport
            ? { width: '96vw', height: '94vh' }
            : { width: 'min(720px, 90vw)', maxHeight: '85vh', height: '85vh' }),
          display: 'flex',
          flexDirection: 'column',
          // The window no longer scrolls itself — the body below does — so
          // the resize grip stays anchored to the bottom-right corner.
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          outline: 'none',
          ...drag.windowStyle,
        }}
      >
        <header
          data-testid="profile-editor-titlebar"
          {...drag.handleProps}
          title={
            drag.smallViewport
              ? undefined
              : 'Drag to move · double-click to reset size and position'
          }
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '18px 24px 12px',
            borderBottom: '1px solid var(--border)',
            flexShrink: 0,
            ...drag.handleProps.style,
          }}
        >
          <h3 style={{ margin: 0 }}>
            {mode === 'create' ? 'New company profile' : `Edit "${profile?.CompanyName}"`}
          </h3>
          <button
            type="button"
            onClick={requestClose}
            // A press on the close button must never start a window drag.
            onPointerDown={(e) => e.stopPropagation()}
            aria-label="Close"
            style={{
              background: 'none',
              border: 'none',
              fontSize: 20,
              cursor: 'pointer',
              color: 'var(--text-muted)',
            }}
          >
            ×
          </button>
        </header>

        <div
          data-testid="profile-editor-body"
          style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '16px 24px 24px' }}
        >

        {/* Tracker row 14 — a refused (stale) save. The form keeps the user's
            edits; nothing is overwritten unless they choose to reload. */}
        {conflict && (
          <SaveConflictBanner
            what="This profile was changed by someone else."
            differences={conflictDifferences()}
            onReload={reloadFromCurrent}
            testId="profile-save-conflict"
          />
        )}
        {error && (
          <div
            role="alert"
            style={{
              color: 'var(--red)',
              background: 'var(--red-bg)',
              padding: '8px 12px',
              borderRadius: 4,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {/* Phase 5.X-company-profile-backend — tab bar */}
        <div
          role="tablist" onKeyDown={tabKeyboard}
          aria-label="Company profile sections"
          style={{
            display: 'flex',
            borderBottom: '1px solid var(--border)',
            marginBottom: 16,
          }}
        >
          {(['profile', 'compliance'] as const).map((tab) => {
            const active = activeTab === tab;
            return (
              <button
                key={tab}
                type="button"
                role="tab"
                id={`profile-tab-${tab}`}
                aria-controls={`profile-tabpanel-${tab}`}
                aria-selected={active} tabIndex={(active) ? 0 : -1}
                data-testid={`profile-tab-${tab}`}
                onClick={() => setActiveTab(tab)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '8px 14px',
                  fontSize: 13,
                  fontWeight: active ? 600 : 400,
                  color: active ? 'var(--accent)' : 'var(--text-muted)',
                  borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
                  cursor: 'pointer',
                  marginBottom: -1,
                }}
              >
                {tab === 'profile' ? 'Company info' : 'Compliance settings'}
              </button>
            );
          })}
        </div>

        {activeTab === 'profile' && (
        <div
          role="tabpanel"
          id="profile-tabpanel-profile"
          aria-labelledby="profile-tab-profile"
        >
        <Section title="Identity">
          <Field label="Company name *" column="CompanyName">
            <input
              type="text"
              value={draft.CompanyName}
              onChange={(e) => update('CompanyName', e.target.value)}
              required
              aria-label="Company name"
              style={inputStyle}
            />
          </Field>
          <Field label="Profile label" column="ProfileName">
            <input
              type="text"
              value={draft.ProfileName}
              onChange={(e) => update('ProfileName', e.target.value)}
              placeholder="Optional — internal label for multi-profile orgs"
              style={inputStyle}
            />
          </Field>
        </Section>

        <Section title="FDA / ESG submission identifiers">
          <Field label="FEI number" column="FeiNumber">
            <input
              type="text"
              value={draft.FeiNumber}
              onChange={(e) => update('FeiNumber', e.target.value)}
              style={inputStyle}
            />
          </Field>
          <Field label="ESG trading partner id" column="FdaEsgTradingPartnerId">
            <input
              type="text"
              value={draft.FdaEsgTradingPartnerId}
              onChange={(e) => update('FdaEsgTradingPartnerId', e.target.value)}
              style={inputStyle}
            />
          </Field>
          <Field label="ESG account" column="EsgAccount">
            <input
              type="text"
              value={draft.EsgAccount}
              onChange={(e) => update('EsgAccount', e.target.value)}
              style={inputStyle}
            />
          </Field>
        </Section>

        <Section title="Address">
          <Field label="Street" column="Street">
            <input
              type="text"
              value={draft.Street}
              onChange={(e) => update('Street', e.target.value)}
              style={inputStyle}
            />
          </Field>
          <Field label="City" column="City">
            <input
              type="text"
              value={draft.City}
              onChange={(e) => update('City', e.target.value)}
              style={inputStyle}
            />
          </Field>
          <Field label="Province / State" column="Province">
            <input
              type="text"
              value={draft.Province}
              onChange={(e) => update('Province', e.target.value)}
              style={inputStyle}
            />
          </Field>
          <Field label="Postal code" column="PostalCode">
            <input
              type="text"
              value={draft.PostalCode}
              onChange={(e) => update('PostalCode', e.target.value)}
              style={inputStyle}
            />
          </Field>
          <Field label="Country" column="Country">
            <input
              type="text"
              value={draft.Country}
              onChange={(e) => update('Country', e.target.value)}
              style={inputStyle}
            />
          </Field>
        </Section>

        <Section title="Contact">
          <Field label="Contact name" column="ContactName">
            <input
              type="text"
              value={draft.ContactName}
              onChange={(e) => update('ContactName', e.target.value)}
              style={inputStyle}
            />
          </Field>
          <Field label="Contact title" column="ContactTitle">
            <input
              type="text"
              value={draft.ContactTitle}
              onChange={(e) => update('ContactTitle', e.target.value)}
              style={inputStyle}
            />
          </Field>
          <Field label="Contact email" column="ContactEmail">
            <input
              type="email"
              value={draft.ContactEmail}
              onChange={(e) => update('ContactEmail', e.target.value)}
              style={inputStyle}
            />
          </Field>
          {/* phase-fix-signatory-and-submission-date — the person authorized to
              sign regulatory correspondence generated from this profile. Its own
              field (not the regulatory contact) so a legal signature block is
              never filled with an unchosen name. Feeds the cover letter's
              authorized_signatory_name / _title. */}
          <Field
            label="Authorized signatory name"
            column="AuthorizedSignatoryName"
            htmlFor="authorized-signatory-name"
          >
            <>
              <input
                type="text"
                id="authorized-signatory-name"
                data-testid="authorized-signatory-name"
                value={draft.AuthorizedSignatoryName}
                onChange={(e) => update('AuthorizedSignatoryName', e.target.value)}
                style={inputStyle}
              />
              <span style={{ display: 'block', marginTop: 3, fontSize: 11, color: 'var(--text-muted)' }}>
                Person authorized to sign regulatory correspondence generated from this profile.
              </span>
            </>
          </Field>
          <Field label="Authorized signatory title" column="AuthorizedSignatoryTitle">
            <input
              type="text"
              data-testid="authorized-signatory-title"
              value={draft.AuthorizedSignatoryTitle}
              onChange={(e) => update('AuthorizedSignatoryTitle', e.target.value)}
              style={inputStyle}
            />
          </Field>
          <Field label="Phone" column="Phone">
            <input
              type="tel"
              value={draft.Phone}
              onChange={(e) => update('Phone', e.target.value)}
              style={inputStyle}
            />
          </Field>
          <Field label="Fax" column="Fax">
            <input
              type="tel"
              value={draft.Fax}
              onChange={(e) => update('Fax', e.target.value)}
              style={inputStyle}
            />
          </Field>
        </Section>

        {/* Phase 5.X-company-profile-backend — Identity extras */}
        <Section title="Identity (extras)">
          <Field label="Trading name" column="TradingName">
            <input
              type="text"
              value={draft.TradingName}
              onChange={(e) => update('TradingName', e.target.value)}
              placeholder="If different from legal name"
              data-testid="profile-trading-name"
              style={inputStyle}
            />
          </Field>
          <Field label="Website" column="Website">
            <input
              type="url"
              value={draft.Website}
              onChange={(e) => update('Website', e.target.value)}
              placeholder="https://example.com"
              data-testid="profile-website"
              style={inputStyle}
            />
          </Field>
          <Field label="Secondary contact name" column="SecondaryContactName">
            <input
              type="text"
              value={draft.SecondaryContactName}
              onChange={(e) => update('SecondaryContactName', e.target.value)}
              data-testid="profile-secondary-contact-name"
              style={inputStyle}
            />
          </Field>
          <Field label="Secondary contact title" column="SecondaryContactTitle">
            <input
              type="text"
              value={draft.SecondaryContactTitle}
              onChange={(e) => update('SecondaryContactTitle', e.target.value)}
              style={inputStyle}
            />
          </Field>
          <Field label="Secondary contact email" column="SecondaryContactEmail">
            <input
              type="email"
              value={draft.SecondaryContactEmail}
              onChange={(e) => update('SecondaryContactEmail', e.target.value)}
              style={inputStyle}
            />
          </Field>
        </Section>

        {/* phase-profile-contact-rows — contacts and signatories are editable
            ROW LISTS with a Function dimension, grouped by region with an
            all-regions group. They replace the one-contact-per-agency-card
            layout. Only shown for a saved profile: rows are children of a
            profile id, so there is nothing to attach them to until it exists. */}
        {profile?.Id && (
          <>
            <ProfileContactRows
              profileId={profile.Id}
              kind="contacts"
              title="Regulatory contacts"
              hint="A submission resolves the contact for its region and function: an exact region+function match first, then that region's primary, then an all-regions row, and finally the profile's general contact fields."
            />
            <div ref={signatoriesRef} id="profile-signatories" data-testid="profile-signatories-anchor">
              <ProfileContactRows
                profileId={profile.Id}
                kind="signatories"
                title="Authorized signatories"
                hint="Different people sign different submission types. The same resolution order applies, ending at the profile's authorized signatory fields."
              />
            </div>
          </>
        )}

        {/* Phase 5.X-company-profile-backend — Region-specific agency details */}
        <fieldset
          data-testid="profile-agency-cards"
          style={{ border: 'none', padding: 0, margin: '0 0 16px 0', minInlineSize: 0 }}
        >
          <legend
            style={{
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: 'var(--text-muted)',
              fontWeight: 700,
              marginBottom: 8,
              padding: 0,
            }}
          >
            Region-specific details
          </legend>
          {AGENCY_REGIONS.map(({ key: region, label }) => {
            const open = agencyOpen[region];
            const draftKey = `${region}AgencyContact` as keyof FormDraft;
            const obj = draft[draftKey] as Record<string, string>;
            return (
              <div
                key={region}
                data-testid={`profile-agency-card-${region}`}
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  marginBottom: 8,
                }}
              >
                <button
                  type="button"
                  data-testid={`profile-agency-toggle-${region}`}
                  onClick={() =>
                    setAgencyOpen((s) => ({ ...s, [region]: !s[region] }))
                  }
                  style={{
                    width: '100%',
                    background: 'none',
                    border: 'none',
                    padding: '8px 12px',
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'var(--text)',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                  aria-expanded={open}
                >
                  <span>{label}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {open ? '▾' : '▸'}
                  </span>
                </button>
                {open && (
                  <div
                    style={{
                      padding: 12,
                      borderTop: '1px solid var(--border)',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                      gap: 12,
                    }}
                  >
                    {AGENCY_CARD_FIELDS[region].map(([field, label]) => (
                      // phase-profile-contact-rows — the contact triple moved
                      // out to the contact ROW list, so what remains here
                      // (QPPV, UK RP, AU local contact) has no derived usage
                      // and stays plain.
                      <Field key={field} label={label}>
                        <input
                          type={field.toLowerCase().includes('email') ? 'email' : 'text'}
                          value={obj[field] ?? ''}
                          onChange={(e) =>
                            updateAgencyField(region, field, e.target.value)
                          }
                          data-testid={`profile-agency-${region}-${field}`}
                          style={inputStyle}
                        />
                      </Field>
                    ))}
                    {/* Agency-specific scalar columns rendered inside the
                        matching card so the operator finds them where
                        they expect rather than in a separate section. */}
                    {/* Phase FIX-company-profile-ux-and-assets: DUNS moved from
                        FDA/ESG section into the FDA agency card. */}
                    {region === 'fda' && (
                      <Field label="DUNS number" column="DunsNumber">
                        <input
                          type="text"
                          value={draft.DunsNumber}
                          onChange={(e) => update('DunsNumber', e.target.value)}
                          data-testid="profile-agency-fda-dunsNumber"
                          style={inputStyle}
                        />
                      </Field>
                    )}
                    {region === 'ema' && (
                      <>
                        <Field label="Applicant Code (EMA)" column="EmaCode">
                          <input
                            type="text"
                            value={draft.EmaCode}
                            onChange={(e) => update('EmaCode', e.target.value)}
                            data-testid="profile-agency-ema-emaCode"
                            style={inputStyle}
                          />
                        </Field>
                        <Field label="EudraVigilance Number" column="Eudravigilance">
                          <input
                            type="text"
                            value={draft.Eudravigilance}
                            onChange={(e) => update('Eudravigilance', e.target.value)}
                            data-testid="profile-agency-ema-eudravigilance"
                            style={inputStyle}
                          />
                        </Field>
                      </>
                    )}
                    {region === 'mhra' && (
                      <>
                        <Field label="UK Licence" column="UkLicence">
                          <input
                            type="text"
                            value={draft.UkLicence}
                            onChange={(e) => update('UkLicence', e.target.value)}
                            data-testid="profile-agency-mhra-ukLicence"
                            style={inputStyle}
                          />
                        </Field>
                        <Field label="UK Representative (Phase 7.1)" column="MhraUkRepresentative">
                          <input
                            type="text"
                            value={draft.MhraUkRepresentative}
                            onChange={(e) => update('MhraUkRepresentative', e.target.value)}
                            data-testid="profile-agency-mhra-ukRepresentative"
                            style={inputStyle}
                          />
                        </Field>
                      </>
                    )}
                    {region === 'pmda' && (
                      <>
                        <Field label="Local Agent Name" column="PmdaLocalAgentName">
                          <input
                            type="text"
                            value={draft.PmdaLocalAgentName}
                            onChange={(e) => update('PmdaLocalAgentName', e.target.value)}
                            data-testid="profile-agency-pmda-localAgentName"
                            style={inputStyle}
                          />
                        </Field>
                        <Field label="Local Agent Address" column="PmdaLocalAgentAddress">
                          <input
                            type="text"
                            value={draft.PmdaLocalAgentAddress}
                            onChange={(e) => update('PmdaLocalAgentAddress', e.target.value)}
                            data-testid="profile-agency-pmda-localAgentAddress"
                            style={inputStyle}
                          />
                        </Field>
                        <Field label="Local Agent Phone" column="PmdaLocalAgentPhone">
                          <input
                            type="tel"
                            value={draft.PmdaLocalAgentPhone}
                            onChange={(e) => update('PmdaLocalAgentPhone', e.target.value)}
                            data-testid="profile-agency-pmda-localAgentPhone"
                            style={inputStyle}
                          />
                        </Field>
                        <Field label="Local Agent Email" column="PmdaLocalAgentEmail">
                          <input
                            type="email"
                            value={draft.PmdaLocalAgentEmail}
                            onChange={(e) => update('PmdaLocalAgentEmail', e.target.value)}
                            data-testid="profile-agency-pmda-localAgentEmail"
                            style={inputStyle}
                          />
                        </Field>
                      </>
                    )}
                    {region === 'tga' && (
                      <>
                        <Field label="TGA Sponsor Name" column="TgaName">
                          <input
                            type="text"
                            value={draft.TgaName}
                            onChange={(e) => update('TgaName', e.target.value)}
                            data-testid="profile-agency-tga-tgaName"
                            style={inputStyle}
                          />
                        </Field>
                        <Field label="TGA Sponsor Number" column="TgaSponsorNumber">
                          <input
                            type="text"
                            value={draft.TgaSponsorNumber}
                            onChange={(e) => update('TgaSponsorNumber', e.target.value)}
                            data-testid="profile-agency-tga-sponsorNumber"
                            style={inputStyle}
                          />
                        </Field>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </fieldset>

        {/* Phase FIX-company-profile-ux-and-assets — Branding section inside modal.
            Only visible when editing an existing profile (not on create, since
            the blob paths require the profile ID). */}
        {mode === 'edit' && profile?.Id && (
          <fieldset
            data-testid="profile-branding-section"
            style={{ border: 'none', padding: 0, margin: '0 0 16px 0', minInlineSize: 0 }}
          >
            <legend
              style={{
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'var(--text-muted)',
                fontWeight: 700,
                marginBottom: 8,
                padding: 0,
              }}
            >
              Branding
            </legend>
            <div
              style={{
                border: '1px solid var(--border)',
                borderRadius: 6,
                padding: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
              }}
            >
              {brandingError && (
                <div role="alert" style={{ color: 'var(--red)', fontSize: 13 }}>
                  {brandingError}
                </div>
              )}

              {/* Company Logo */}
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Company Logo</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
                  PNG, JPG, or SVG — max 2 MB
                </div>
                {brandingLogoUrl ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                    {/* phase-ui-company-profiles-improvements (Item 2) — this
                        used to point at /logo-preview, a route that never
                        existed (the thumbnail silently error-hid). Now served
                        by the real tenant-scoped branding endpoint. */}
                    <img
                      src={v2.companyProfiles.brandingUrl(profile.Id, 'logo')}
                      alt="Company logo"
                      data-testid="profile-logo-thumbnail"
                      style={{ maxWidth: 120, maxHeight: 60, objectFit: 'contain', border: '1px solid var(--border)', borderRadius: 4, padding: 4 }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <span style={{ fontSize: 12, color: 'var(--text-muted)', wordBreak: 'break-all' }}>
                      {brandingLogoUrl.split('/').pop() ?? 'logo'}
                    </span>
                    {canManageBranding && (
                      <button
                        type="button"
                        data-testid="profile-logo-remove-btn"
                        onClick={() => void handleLogoDelete()}
                        disabled={brandingUploading}
                        style={{
                          background: 'none',
                          color: 'var(--red)',
                          border: '1px solid var(--red)',
                          borderRadius: 4,
                          padding: '3px 8px',
                          fontSize: 12,
                          cursor: 'pointer',
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
                    None
                  </div>
                )}
                {canManageBranding && (
                  <>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/svg+xml"
                      style={{ display: 'none' }}
                      data-testid="profile-logo-input"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) await handleLogoUpload(file);
                        if (logoInputRef.current) logoInputRef.current.value = '';
                      }}
                    />
                    <button
                      type="button"
                      data-testid="profile-logo-upload-btn"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={brandingUploading}
                      style={{
                        background: 'var(--accent)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 4,
                        padding: '5px 10px',
                        fontSize: 12,
                        cursor: brandingUploading ? 'wait' : 'pointer',
                        opacity: brandingUploading ? 0.6 : 1,
                      }}
                    >
                      {brandingLogoUrl ? 'Replace logo' : 'Upload logo'}
                    </button>
                  </>
                )}
              </div>

              {/* Letterhead */}
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Default letterhead</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
                  PDF or DOCX — max 10 MB. Used for every region without its own letterhead
                  (regional letterheads below). Removing it leaves those regions with no letterhead.
                </div>
                {defaultRegional && (
                  <div
                    data-testid="profile-default-letterhead-source"
                    style={{ fontSize: 12, marginBottom: 8 }}
                  >
                    The default is the <strong>{regionLabel(defaultRegional.Region)}</strong> letterhead
                    {defaultRegional.Label ? ` (${defaultRegional.Label})` : ''}.
                  </div>
                )}
                {brandingLetterheadUrl ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)', wordBreak: 'break-all' }} data-testid="profile-letterhead-filename">
                      {brandingLetterheadUrl.split('/').pop() ?? 'letterhead'}
                    </span>
                    {/* phase-ui-company-profiles-improvements (Item 2) — see the
                        current letterhead, not just replace it blind. PDF opens
                        inline in a new tab; DOCX downloads (browsers can't
                        render it) — the endpoint sets the disposition. */}
                    {profile && (
                      <a
                        href={v2.companyProfiles.brandingUrl(profile.Id, 'letterhead')}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="profile-letterhead-view-link"
                        style={{ fontSize: 12, color: 'var(--accent)' }}
                      >
                        {brandingLetterheadUrl.toLowerCase().endsWith('.docx') ? 'Download' : 'View'}
                      </a>
                    )}
                    {canManageBranding && (
                      <button
                        type="button"
                        data-testid="profile-letterhead-remove-btn"
                        onClick={() => void handleLetterheadDelete()}
                        disabled={brandingUploading}
                        style={{
                          background: 'none',
                          color: 'var(--red)',
                          border: '1px solid var(--red)',
                          borderRadius: 4,
                          padding: '3px 8px',
                          fontSize: 12,
                          cursor: 'pointer',
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
                    None
                  </div>
                )}
                {canManageBranding && (
                  <>
                    <input
                      ref={letterheadInputRef}
                      type="file"
                      accept="application/pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      style={{ display: 'none' }}
                      data-testid="profile-letterhead-input"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) await handleLetterheadUpload(file);
                        if (letterheadInputRef.current) letterheadInputRef.current.value = '';
                      }}
                    />
                    <button
                      type="button"
                      data-testid="profile-letterhead-upload-btn"
                      onClick={() => letterheadInputRef.current?.click()}
                      disabled={brandingUploading}
                      style={{
                        background: 'var(--accent)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 4,
                        padding: '5px 10px',
                        fontSize: 12,
                        cursor: brandingUploading ? 'wait' : 'pointer',
                        opacity: brandingUploading ? 0.6 : 1,
                      }}
                    >
                      {brandingLetterheadUrl ? 'Replace letterhead' : 'Upload letterhead'}
                    </button>
                  </>
                )}
                {/* phase-ui-company-profiles-improvements (Item 5) — starter
                    Word letterhead matching the generator's real geometry
                    (US Letter, full-width top artwork band, 60/72pt body
                    margins — sourced from the rendering code, not invented). */}
                <div style={{ marginTop: 8 }}>
                  <a
                    href={v2.companyProfiles.letterheadStarterTemplateUrl(profile?.Id)}
                    download="letterhead-starter-template.docx"
                    data-testid="letterhead-starter-template-link"
                    style={{ fontSize: 12, color: 'var(--accent)' }}
                  >
                    ⬇ Download starter template (.docx)
                  </a>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                    A Word starter laid out to the generator&apos;s page geometry — build your
                    letterhead in it so artwork won&apos;t collide with generated text.
                  </div>
                </div>
              </div>

              {/* Letterhead mode — phase-small-findings-batch-1 (item 3).
                  The three options used to carry a single line of help
                  ("Controls how cover letters are rendered for this company")
                  that said what the control was FOR without saying what any
                  option DOES, and nothing reacted when a mode was chosen with
                  no file uploaded. Both are addressed here, and the help text
                  is written from the code (see LETTERHEAD_MODE_HELP). */}
              <div>
                <label
                  style={{ fontSize: 13, color: 'var(--text-muted)', display: 'block' }}
                >
                  <span style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: 'var(--text)' }}>
                    Letterhead mode
                  </span>
                  <select
                    data-testid="profile-letterhead-mode"
                    value={letterheadMode}
                    disabled={letterheadModeSaving}
                    onChange={(e) => void handleLetterheadModeChange(e.target.value)}
                    style={{
                      padding: '6px 10px',
                      border: '1px solid var(--border)',
                      borderRadius: 6,
                      fontSize: 13,
                      background: 'var(--surface)',
                      color: 'var(--text)',
                    }}
                  >
                    {LETTERHEAD_MODES.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </label>
                {letterheadModeSaving && (
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>Saving…</span>
                )}
                {letterheadModeSaved && !letterheadModeSaving && (
                  <span
                    data-testid="profile-letterhead-mode-saved"
                    style={{ fontSize: 11, color: 'var(--green, #059669)', marginLeft: 8 }}
                  >
                    ✓ Saved
                  </span>
                )}

                {/* Every mode, and what each one means — not just the one
                    currently selected, so the choice can be made informed. */}
                <ul
                  data-testid="profile-letterhead-mode-help"
                  style={{ fontSize: 11, color: 'var(--text-muted)', margin: '8px 0 0', paddingLeft: 18, lineHeight: 1.6 }}
                >
                  {LETTERHEAD_MODES.map((m) => (
                    <li key={m.value} style={{ fontWeight: m.value === letterheadMode ? 600 : 400 }}>
                      <strong>{m.label}</strong> — {m.help}
                      {m.requires && ` Needs ${m.requires === 'logo' ? 'a company logo' : 'a letterhead file'} uploaded above.`}
                    </li>
                  ))}
                </ul>

                {/* The missing-asset state, named plainly. A mode whose asset
                    is absent is not an error — it simply cannot take effect,
                    and saying nothing was the "silent nonsense state". */}
                {letterheadModeGap && (
                  <div
                    role="status"
                    data-testid="profile-letterhead-mode-gap"
                    style={{
                      marginTop: 8,
                      padding: '6px 10px',
                      borderRadius: 6,
                      background: 'var(--amber-bg, #FFF8E1)',
                      border: '1px solid var(--amber, #E0A800)',
                      fontSize: 11,
                      lineHeight: 1.5,
                    }}
                  >
                    {letterheadModeGap}
                  </div>
                )}

                {/* What actually drives a generated cover letter. Written
                    from the code: phase-company-profile-rows-7-9 wired the
                    profile letterheads into generation for "Full letterhead"
                    (src/routes/cover-letter.ts → resolveProfileLetterhead);
                    every other mode still renders through the ORG settings +
                    the org's active letterhead asset, exactly as before. */}
                <div
                  data-testid="profile-letterhead-mode-scope-note"
                  style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.5 }}
                >
                  With <strong>Full letterhead</strong>, a generated cover letter is laid onto
                  this company&apos;s letterhead for the submission&apos;s region (a regional
                  variant below), otherwise its default letterhead; the result names which one
                  was used. With the other modes, rendering is driven by the organisation-wide
                  settings <em>Cover letter letterhead mode</em> (Document Generation) and{' '}
                  <em>Apply letterhead to generated documents</em> (Branding), together with the
                  organisation&apos;s active letterhead asset.
                </div>
              </div>

              {/* phase-small-findings-batch-1 (item 4) — see the letterhead
                  before committing to it. Read-only: this reuses the Form
                  Templates preview endpoints, which substitute deterministic
                  sample values, stamp PREVIEW — SAMPLE DATA, run NO model call
                  and persist nothing. */}
              <div data-testid="profile-cover-letter-preview">
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
                  Preview cover letter
                </div>
                {coverLetterTemplates.length === 0 ? (
                  <div
                    data-testid="profile-cover-letter-preview-none"
                    style={{ fontSize: 12, color: 'var(--text-muted)' }}
                  >
                    No cover-letter template is available to preview. Add one under
                    Form Templates, then return here.
                  </div>
                ) : (
                  <>
                    {coverLetterTemplates.length > 1 && (
                      <select
                        data-testid="profile-cover-letter-template-select"
                        aria-label="Cover letter template to preview"
                        value={coverLetterTemplateId}
                        onChange={(e) => setCoverLetterTemplateId(e.target.value)}
                        style={{
                          padding: '5px 8px',
                          border: '1px solid var(--border)',
                          borderRadius: 6,
                          fontSize: 12,
                          marginRight: 8,
                          background: 'var(--surface)',
                          color: 'var(--text)',
                        }}
                      >
                        {coverLetterTemplates.map((tpl) => (
                          <option key={tpl.Id} value={tpl.Id}>
                            {tpl.Name}
                          </option>
                        ))}
                      </select>
                    )}
                    <button
                      type="button"
                      data-testid="profile-cover-letter-preview-btn"
                      onClick={() => setPreviewTemplate(
                        coverLetterTemplates.find((tpl) => tpl.Id === coverLetterTemplateId)
                          ?? coverLetterTemplates[0]
                          ?? null,
                      )}
                      style={{
                        background: 'none',
                        border: '1px solid var(--border)',
                        borderRadius: 4,
                        padding: '5px 10px',
                        fontSize: 12,
                        cursor: 'pointer',
                      }}
                    >
                      Preview cover letter
                    </button>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                      Read-only, watermarked <em>PREVIEW — SAMPLE DATA</em>. This
                      company&apos;s profile values are filled in; nothing is generated,
                      saved or sent, and no AI call is made.
                    </div>
                  </>
                )}
              </div>

              {/* phase-regional-letterhead-templates (TODO-077) */}
              {profile?.Id && (
                <div data-testid="regional-letterheads-section" style={{ gridColumn: '1 / -1', marginTop: 12 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
                    Regional letterheads
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
                    One letterhead per region. A cover letter for a submission in that region is
                    laid onto it (with Letterhead mode “Full letterhead”); every other region uses the
                    default letterhead. Any uploaded letterhead can be made the default — there is
                    only ever one, so choosing a new default replaces the previous one. The default
                    cannot be deleted while it is the default.
                  </div>
                  {regionalLoading && (
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading…</div>
                  )}
                  {regionalError && (
                    <div role="alert" data-testid="regional-letterheads-error" style={{ fontSize: 12, color: 'var(--red)', marginBottom: 8 }}>
                      {regionalError}
                    </div>
                  )}
                  {!regionalLoading && regionalLetterheads.length > 0 && (
                    <table data-testid="regional-letterheads-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 10 }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                          <th style={{ padding: '6px 8px', width: 110 }}>Region</th>
                          <th style={{ padding: '6px 8px' }}>Label</th>
                          <th style={{ padding: '6px 8px' }}>File</th>
                          <th style={{ padding: '6px 8px', width: 190 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {regionalLetterheads.map((r) => {
                          const isDefault = r.LetterheadUrl === brandingLetterheadUrl;
                          // Stored files are served by the tenant-scoped file
                          // endpoint; an external URL is linked as-is.
                          const stored = r.IsStoredFile ?? !/^https?:\/\//i.test(r.LetterheadUrl);
                          const href = stored && profile
                            ? v2.companyProfiles.regionalLetterheadFileUrl(profile.Id, r.Id)
                            : r.LetterheadUrl;
                          return (
                            <tr key={r.Id} data-testid={`regional-letterhead-row-${r.Region}`} style={{ borderBottom: '1px solid var(--border)' }}>
                              <td style={{ padding: '6px 8px', fontWeight: 600 }}>
                                {regionLabel(r.Region)}
                                {isDefault && (
                                  <span
                                    data-testid={`regional-letterhead-default-badge-${r.Region}`}
                                    style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 10, background: 'var(--accent)', color: '#fff' }}
                                  >
                                    Default
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: '6px 8px', color: 'var(--text-muted)' }}>{r.Label ?? '—'}</td>
                              <td style={{ padding: '6px 8px', color: 'var(--text-muted)', wordBreak: 'break-all', fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: 11 }}>
                                {stored ? (r.LetterheadUrl.split('/').pop() ?? r.LetterheadUrl) : r.LetterheadUrl}
                                {!stored && (
                                  <div style={{ fontFamily: 'inherit', fontSize: 10, color: 'var(--amber, #b45309)' }}>
                                    External URL — not used for generation; upload the file instead.
                                  </div>
                                )}
                              </td>
                              <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                                <a
                                  href={href}
                                  target="_blank"
                                  rel="noreferrer"
                                  data-testid={`regional-letterhead-view-${r.Region}`}
                                  style={{ fontSize: 11, color: 'var(--accent)', marginRight: 8 }}
                                >
                                  {r.LetterheadUrl.toLowerCase().endsWith('.docx') ? 'Download' : 'View'}
                                </a>
                                {canManageBranding && stored && !isDefault && (
                                  <button type="button" data-testid={`regional-letterhead-set-default-${r.Region}`} onClick={() => void handleSetDefaultLetterhead(r)} style={{ background: 'none', color: 'var(--accent)', border: '1px solid var(--accent)', borderRadius: 4, padding: '2px 8px', fontSize: 11, cursor: 'pointer', marginRight: 6 }}>Set as default</button>
                                )}
                                {canManageBranding && (
                                  <button
                                    type="button"
                                    data-testid={`regional-letterhead-delete-${r.Region}`}
                                    onClick={() => void handleDeleteRegionalLetterhead(r.Id)}
                                    disabled={isDefault}
                                    title={isDefault ? 'This is the default letterhead. Set another letterhead as the default first.' : undefined}
                                    style={{ background: 'none', color: 'var(--red)', border: '1px solid var(--red)', borderRadius: 4, padding: '2px 8px', fontSize: 11, cursor: isDefault ? 'not-allowed' : 'pointer', opacity: isDefault ? 0.5 : 1 }}
                                  >
                                    Delete
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                  {!regionalLoading && regionalLetterheads.length === 0 && !showAddRegional && (
                    <div data-testid="regional-letterheads-empty" style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
                      No regional letterheads yet — the default letterhead is used for all regions.
                    </div>
                  )}
                  {canManageBranding && !showAddRegional && (
                    <button type="button" data-testid="regional-letterhead-add-btn" onClick={() => setShowAddRegional(true)} style={{ background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 4, padding: '5px 10px', fontSize: 12, cursor: 'pointer' }}>+ Add regional letterhead</button>
                  )}
                  {canManageBranding && showAddRegional && (
                    <div data-testid="regional-letterhead-add-form" style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 10, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface-alt, #f9fafb)' }}>
                      <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        Region
                        {/* Canonical region codes (the server files every row
                            under one); "Default" is not a region — any
                            letterhead can be made the default instead. */}
                        <select data-testid="regional-letterhead-region-select" value={draftRegion} onChange={(e) => setDraftRegion(e.target.value)} style={{ display: 'block', marginTop: 4, padding: '5px 8px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 13, background: 'var(--surface)', color: 'var(--text)' }}>
                          {LETTERHEAD_REGIONS.map((code) => (
                            <option key={code} value={code}>{regionLabel(code)}</option>
                          ))}
                        </select>
                      </label>
                      <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        Label (optional)
                        <input type="text" data-testid="regional-letterhead-label-input" value={draftLabel} onChange={(e) => setDraftLabel(e.target.value)} placeholder="e.g. FDA Regulatory Affairs Letterhead" maxLength={255} style={{ display: 'block', marginTop: 4, width: '100%', padding: '5px 8px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 13, boxSizing: 'border-box' }} />
                      </label>
                      <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        Letterhead file (PDF or DOCX — max 10 MB)
                        <input
                          ref={regionalFileRef}
                          type="file"
                          data-testid="regional-letterhead-file-input"
                          accept="application/pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                          onChange={(e) => setDraftFile(e.target.files?.[0] ?? null)}
                          style={{ display: 'block', marginTop: 4, fontSize: 12 }}
                        />
                      </label>
                      <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        …or a storage key for a letterhead already in this organisation's assets
                        <input type="text" data-testid="regional-letterhead-url-input" value={draftLetterheadUrl} onChange={(e) => setDraftLetterheadUrl(e.target.value)} placeholder="storage key, e.g. company-assets/<tenant>/letterhead.pdf" maxLength={1000} disabled={draftFile !== null} style={{ display: 'block', marginTop: 4, width: '100%', padding: '5px 8px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 13, fontFamily: 'ui-monospace, SFMono-Regular, monospace', boxSizing: 'border-box' }} />
                      </label>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button type="button" onClick={() => { setShowAddRegional(false); setDraftLetterheadUrl(''); setDraftLabel(''); setDraftFile(null); setRegionalError(null); }} disabled={savingRegional} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 4, padding: '5px 10px', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                        <button type="button" data-testid="regional-letterhead-save-btn" onClick={() => void handleSaveRegionalLetterhead()} disabled={savingRegional} style={{ background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 4, padding: '5px 10px', fontSize: 12, cursor: savingRegional ? 'wait' : 'pointer', opacity: savingRegional ? 0.6 : 1 }}>{savingRegional ? 'Saving…' : 'Save'}</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </fieldset>
        )}
        {/* phase-small-findings-batch-1 (item 4) — the shared preview surface,
            opened already scoped to THIS company profile. */}
        {previewTemplate && profile?.Id && (
          <TemplatePreviewModal
            template={previewTemplate}
            initialProfileId={profile.Id}
            onClose={() => setPreviewTemplate(null)}
          />
        )}
        </div>
        )}

        {activeTab === 'compliance' && (
          <div
            role="tabpanel"
            id="profile-tabpanel-compliance"
            aria-labelledby="profile-tab-compliance"
          >
            <ComplianceTab draft={draft} update={update} />
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={requestClose}
            style={{
              background: 'none',
              color: 'var(--text-muted)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              padding: '8px 14px',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            style={{
              background: 'var(--accent)',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              padding: '8px 14px',
              fontSize: 13,
              fontWeight: 600,
              cursor: saving ? 'wait' : 'pointer',
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? 'Saving…' : mode === 'create' ? 'Create profile' : 'Save changes'}
          </button>
        </div>
        </div>

        {/* Bottom-right resize grip — hidden in the small-viewport fallback. */}
        {!drag.smallViewport && (
          <div
            data-testid="profile-editor-resize-grip"
            aria-hidden="true"
            {...drag.resizeHandleProps}
            style={{ ...RESIZE_GRIP_STYLE, ...drag.resizeHandleProps.style }}
          />
        )}
      </form>
    </div>
    </FieldUsageContext.Provider>
  );
}

function Section({ title, children }: { title: string; children: ReactElement[] }): ReactElement {
  return (
    <fieldset
      style={{
        border: 'none',
        padding: 0,
        margin: '0 0 16px 0',
        // fieldset defaults to min-inline-size:min-content — must shrink
        // with the resizable editor window instead of forcing a scrollbar.
        minInlineSize: 0,
      }}
    >
      <legend
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: 'var(--text-muted)',
          fontWeight: 700,
          marginBottom: 8,
          padding: 0,
        }}
      >
        {title}
      </legend>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 12,
        }}
      >
        {children}
      </div>
    </fieldset>
  );
}


// Phase 5.X-company-profile-backend — Compliance Settings tab.
//
// Three nullable booleans. The form models each as a tri-state select
// (Inherit / On / Off) so the operator can express "no org override"
// (null) distinct from explicit on/off. When `Inherit` is selected the
// row sends null on save and the publish-governance layer falls back
// to the existing Settings tier.

interface ComplianceTabProps {
  readonly draft: FormDraft;
  readonly update: <K extends keyof FormDraft>(k: K, v: FormDraft[K]) => void;
}

const COMPLIANCE_TOGGLES: ReadonlyArray<{
  key: 'RequireDualControlDelete' | 'RequirePublishAttestation' | 'RequireReauthAtPublish';
  label: string;
  description: string;
}> = [
  // phase-entity-model-stage-1-delete-safety — "Require dual-control delete"
  // is REMOVED from this list. It read as a compliance control and was
  // consulted by nothing, including the delete route itself (its two
  // siblings ARE read, by publishGovernance.ts — this one never was).
  //
  // It was removed rather than "made real" because real dual control means a
  // SECOND PERSON approves — an approval workflow with a pending-deletion
  // record and a reviewer, not a toggle. The only cheap implementation would
  // have been a second confirmation by the SAME person, which is not dual
  // control and would simply be a new reassuring surface: the exact defect
  // being fixed. What the toggle gestured at is now covered by real
  // machinery: a profile in use CANNOT be deleted (the route refuses and
  // names the submissions), deletion is soft, and undelete is visible.
  //
  // The database column is deliberately KEPT (retain-don't-delete): no
  // stored value is destroyed, and a genuine dual-control build can adopt it.
  {
    key: 'RequirePublishAttestation',
    label: 'Require publish attestation',
    description:
      'Operators must check a pre-publish checklist before compile. Inherit uses the Settings-tier value.',
  },
  {
    key: 'RequireReauthAtPublish',
    label: 'Require re-authentication at publish',
    description:
      'Operators must re-authenticate (acquireTokenInteractive prompt=login) before each compile. Inherit uses the Settings-tier value.',
  },
];

function ComplianceTab({ draft, update }: ComplianceTabProps): ReactElement {
  return (
    <div data-testid="profile-compliance-tab">
      <div
        style={{
          background: 'rgba(59,130,246,0.08)',
          border: '1px solid rgba(59,130,246,0.25)',
          borderRadius: 6,
          padding: '10px 14px',
          marginBottom: 16,
          fontSize: 13,
          color: 'var(--text)',
        }}
      >
        Org-level overrides. <strong>Inherit</strong> uses the value from the
        organization Settings tier (the existing default-on machinery in
        the publish-governance flow). Set to <strong>On</strong> or{' '}
        <strong>Off</strong> to override it for this company profile.
        <div style={{ marginTop: 8 }} data-testid="profile-compliance-dual-control-note">
          <strong>Deleting a company profile</strong> is controlled directly,
          not by a setting: a profile that any submission still links to
          cannot be deleted — the system refuses and names those submissions.
          Deletion is reversible, and deleted profiles can be restored from
          the “Show deleted” view.
        </div>
      </div>
      {COMPLIANCE_TOGGLES.map(({ key, label, description }) => (
        <div
          key={key}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 0',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <div style={{ flex: 1, paddingRight: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 500 }}>{label}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              {description}
            </div>
          </div>
          <select
            value={draft[key]}
            onChange={(e) =>
              update(key, e.target.value as '' | 'true' | 'false')
            }
            data-testid={`profile-compliance-${key}`}
            style={{
              padding: '6px 10px',
              border: '1px solid var(--border)',
              borderRadius: 6,
              fontSize: 13,
              minWidth: 110,
              background: 'var(--surface)',
              color: 'var(--text)',
            }}
          >
            <option value="">Inherit</option>
            <option value="true">On</option>
            <option value="false">Off</option>
          </select>
        </div>
      ))}
    </div>
  );
}
